import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, Plane } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { TagCloud } from "@/components/tag-cloud";
import { ThemeToggle } from "@/components/theme-toggle";

export function Header() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <header className="glass">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <a href="/" title="Tanie Latanie — strona główna" className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-sunset shadow-glow">
            <Plane className="h-5 w-5 text-primary-foreground -rotate-45" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg font-bold">Tanie Latanie</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              tanielatanie.pl
            </div>
          </div>
        </a>
        <div className="flex items-center gap-2">
          <nav className="hidden gap-6 text-sm md:flex md:mr-2">
            <Link to="/loty" className="text-muted-foreground transition-colors hover:text-foreground" activeProps={{ className: "text-foreground" }}>Loty z Polski</Link>
            <Link to="/" className="text-muted-foreground transition-colors hover:text-foreground" activeOptions={{ exact: true }} activeProps={{ className: "text-foreground" }}>
              Wyszukaj
            </Link>
            <Link to="/destinations" className="text-muted-foreground transition-colors hover:text-foreground" activeProps={{ className: "text-foreground" }}>
              Kierunki
            </Link>
            <Link to="/okazje" className="text-muted-foreground transition-colors hover:text-foreground" activeProps={{ className: "text-foreground" }}>
              Okazje
            </Link>
            <Link to="/gdziekolwiek" onClick={() => { void import("@/lib/analytics").then(({ trackAnywhereIntent }) => trackAnywhereIntent({ source: "nav" })).catch(() => {}); }} className="text-muted-foreground transition-colors hover:text-foreground" activeProps={{ className: "text-foreground" }}>
              Gdziekolwiek
            </Link>
          </nav>
          <ThemeToggle />
          {/* Mobile hamburger menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Otwórz menu"
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-foreground transition hover:bg-white/10 md:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 max-w-[85vw]">
            <VisuallyHidden>
              <SheetTitle>Menu</SheetTitle>
              <SheetDescription>Nawigacja serwisu</SheetDescription>
            </VisuallyHidden>
            <nav className="mt-8 flex flex-col gap-1 text-base">
              <Link to="/" onClick={close} activeOptions={{ exact: true }} activeProps={{ className: "bg-accent/30 text-foreground" }} className="rounded-lg px-3 py-3 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">Wyszukaj</Link>
              <Link to="/destinations" onClick={close} activeProps={{ className: "bg-accent/30 text-foreground" }} className="rounded-lg px-3 py-3 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">Kierunki</Link>
              <Link to="/okazje" onClick={close} activeProps={{ className: "bg-accent/30 text-foreground" }} className="rounded-lg px-3 py-3 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">Okazje</Link>
              <Link to="/gdziekolwiek" onClick={close} activeProps={{ className: "bg-accent/30 text-foreground" }} className="rounded-lg px-3 py-3 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">Gdziekolwiek</Link>
              <Link to="/tanie-loty" onClick={close} className="rounded-lg px-3 py-3 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">Tanie loty</Link>
              <Link to="/loty" onClick={close} className="rounded-lg px-3 py-3 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">Loty z Polski</Link>
              <Link to="/blog" onClick={close} className="rounded-lg px-3 py-3 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">Blog</Link>
              <Link to="/about" onClick={close} className="rounded-lg px-3 py-3 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">O nas</Link>
              <Link to="/contact" onClick={close} className="rounded-lg px-3 py-3 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">Kontakt</Link>
            </nav>
          </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}


export function Footer() {
  const openCookies = () => {
    try {
      localStorage.removeItem("cookie-consent");
    } catch {}
    window.dispatchEvent(new Event("open-cookie-settings"));
    alert("Ustawienia cookies zostały zresetowane. Odśwież stronę, aby ponownie wybrać preferencje.");
  };
  return (
    <footer className="border-t border-border/50">
      <TagCloud />
      <div className="container mx-auto flex flex-col items-center gap-3 px-4 pb-10 pt-6 text-center text-sm text-muted-foreground">
        <div className="font-display text-base font-semibold text-foreground">Tanie Latanie</div>
        <div>Porównaj loty i zaplanuj podróż</div>
        <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          <Link to="/tanie-loty" className="hover:text-foreground">Tanie loty</Link>
          <span className="opacity-40">·</span>
          <Link to="/gdziekolwiek" className="hover:text-foreground">Gdziekolwiek</Link>
          <span className="opacity-40">·</span>
          <Link to="/loty" className="hover:text-foreground">Wszystkie kierunki</Link>
          <span className="opacity-40">·</span>
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <span className="opacity-40">·</span>
          <Link to="/about" className="hover:text-foreground">O nas</Link>
          <span className="opacity-40">·</span>
          <Link to="/contact" className="hover:text-foreground">Kontakt</Link>
          <span className="opacity-40">·</span>
          <Link to="/polityka-prywatnosci" className="hover:text-foreground">Polityka prywatności</Link>
          <span className="opacity-40">·</span>
          <Link to="/regulamin" className="hover:text-foreground">Regulamin</Link>
          <span className="opacity-40">·</span>
          <a href="/sitemap.xml" className="hover:text-foreground">Mapa strony</a>
          <span className="opacity-40">·</span>
          <button type="button" onClick={openCookies} className="hover:text-foreground underline-offset-4 hover:underline">
            Ustawienia cookies
          </button>
        </nav>
        <div>© {new Date().getFullYear()} tanielatanie.pl — wszystkie ceny w PLN.</div>
      </div>
    </footer>
  );
}
